/**
 * BrandPreferencesPage — the "Brand & product preferences" surface (T12.2 / 🏷️).
 *
 * Two household-wide preference families, both never hard filters:
 *   - Brands you like / avoid — against the managed brand catalog (mapped to a
 *     name from the small in-memory brand list via `useBrands()`).
 *   - Products you like / avoid — against the SystemProduct catalog. The catalog
 *     is too large to fetch wholesale, so the add-control searches the server
 *     (mirroring the per-product `SystemProductPicker` search) and the linked
 *     rows embed their display name.
 *
 * In each family a thing is like OR avoid, never both — the backend's composite
 * PK + the `set*Preference` flip enforce it; the UI mirrors by flipping the role
 * in place when the other list picks an already-linked item. Every write rides
 * the `shoppinglistevents` projection spine and invalidates the matching
 * household-preferences query so the lists reflect the new state and survive
 * reload.
 */

import { useMemo, useState } from 'react';
import {
  useBrands,
  useHouseholdBrandPreferences,
  useLinkHouseholdBrandPreference,
  useSetHouseholdBrandPreference,
  useUnlinkHouseholdBrandPreference,
  useHouseholdSystemProductPreferences,
  useLinkHouseholdSystemProductPreference,
  useSetHouseholdSystemProductPreference,
  useUnlinkHouseholdSystemProductPreference,
} from '@shopista/shared/api/hooks';
import { apiErrorMessage } from '@shopista/shared/api';
import { Spinner } from '../../components';
import { SettingsLayover } from './SettingsLayover';
import {
  setgroupStyle,
  h4Style,
  hintStyle,
  tagsRowStyle,
  RemovableChip,
} from './prefControls';
import { InlineAddSearch, type AddOption } from '../products/InlineAddSearch';
import { SystemProductAddSearch } from './SystemProductAddSearch';

const errorStyle = {
  color: 'var(--color-status-danger)',
  fontSize: 'var(--font-size-sm)',
} as const;

const mutedStyle = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
} as const;

// MN6 — the add-search is a full-width sibling block BELOW the chips row, not a
// tall column injected mid-`flex-wrap`. The chips keep `tagsRowStyle`; this
// block spans the full content column so the open search input + its results
// list read as a left-aligned vertical stack at every width.
const addBlockStyle = {
  width: '100%',
  marginTop: 'var(--space-2)',
} as const;

export function BrandPreferencesPage(): JSX.Element {
  const prefsQuery = useHouseholdBrandPreferences();
  const brandsQuery = useBrands();
  const linkPref = useLinkHouseholdBrandPreference();
  const setPref = useSetHouseholdBrandPreference();
  const unlinkPref = useUnlinkHouseholdBrandPreference();

  const spPrefsQuery = useHouseholdSystemProductPreferences();
  const linkSp = useLinkHouseholdSystemProductPreference();
  const setSp = useSetHouseholdSystemProductPreference();
  const unlinkSp = useUnlinkHouseholdSystemProductPreference();

  const [actionError, setActionError] = useState<string | null>(null);

  const brands = brandsQuery.data?.brands ?? [];
  const prefs = prefsQuery.data?.preferences ?? [];
  const spPrefs = spPrefsQuery.data?.preferences ?? [];

  const brandName = useMemo(() => {
    const map = new Map(brands.map((b) => [b.id, b.name]));
    return (id: string) => map.get(id) ?? id;
  }, [brands]);

  const run = async (fn: () => Promise<unknown>) => {
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Could not update the preference.'));
    }
  };

  const band = {
    variant: 'steers' as const,
    children: (
      <>
        Mark the brands and products your household{' '}
        <strong>likes</strong> or wants to <strong>avoid</strong>. Avoided items
        are <strong>never hidden</strong> — search always finds them.
      </>
    ),
  };

  if (prefsQuery.isLoading || spPrefsQuery.isLoading) {
    return (
      <SettingsLayover
        icon="bookmark"
        title="Brand & product preferences"
        backLabel="Back"
        testId="settings-brands"
        band={band}
      >
        <Spinner />
      </SettingsLayover>
    );
  }

  // --- Brands ---------------------------------------------------------------
  const liked = prefs.filter((p) => p.preference === 'like');
  const avoided = prefs.filter((p) => p.preference === 'avoid');
  const linkedIds = new Set(prefs.map((p) => p.brand_id));
  const availableOptions: AddOption[] = brands
    .filter((b) => !linkedIds.has(b.id))
    .map((b) => ({ id: b.id, label: b.name }));

  // Adding to one list when the brand is already in the other flips the role in
  // place (a brand is like OR avoid, never both).
  const addBrand = (brandId: string, preference: 'like' | 'avoid') => {
    const existing = prefs.find((p) => p.brand_id === brandId);
    if (existing) {
      void run(() => setPref.mutateAsync({ brandId, preference }));
    } else {
      void run(() => linkPref.mutateAsync({ brandId, preference }));
    }
  };

  // Avoid-list options also include liked brands (picking one flips it).
  const likeOptions = availableOptions.concat(
    avoided.map((p) => ({ id: p.brand_id, label: brandName(p.brand_id) }))
  );
  const avoidOptions = availableOptions.concat(
    liked.map((p) => ({ id: p.brand_id, label: brandName(p.brand_id) }))
  );

  // --- Products (SystemProducts) -------------------------------------------
  const spLiked = spPrefs.filter((p) => p.preference === 'like');
  const spAvoided = spPrefs.filter((p) => p.preference === 'avoid');
  const spLinkedIds = new Set(spPrefs.map((p) => p.systemproduct_id));

  // Adding to one list when the product is already in the other flips the role
  // in place (a product is like OR avoid, never both).
  const addSystemProduct = (systemProductId: string, preference: 'like' | 'avoid') => {
    const existing = spPrefs.find((p) => p.systemproduct_id === systemProductId);
    if (existing) {
      void run(() => setSp.mutateAsync({ systemProductId, preference }));
    } else {
      void run(() => linkSp.mutateAsync({ systemProductId, preference }));
    }
  };

  return (
    <SettingsLayover
      icon="bookmark"
      title="Brand & product preferences"
      backLabel="Back"
      testId="settings-brands"
      band={band}
    >
      {actionError && (
        <div role="alert" style={errorStyle} data-testid="settings-brands-error">
          {actionError}
        </div>
      )}

      <section style={setgroupStyle} data-testid="settings-brands-like">
        <h4 style={h4Style}>Brands you like</h4>
        <p style={hintStyle}>
          Brands your household reaches for — unless a specific product says
          otherwise.
        </p>
        <div style={tagsRowStyle}>
          {liked.length === 0 && (
            <span style={mutedStyle} data-testid="settings-brands-like-empty">
              No liked brands yet.
            </span>
          )}
          {liked.map((p) => (
            <RemovableChip
              key={p.brand_id}
              label={brandName(p.brand_id)}
              variant="like"
              onRemove={() =>
                void run(() => unlinkPref.mutateAsync(p.brand_id))
              }
              testId={`settings-brands-like-${p.brand_id}`}
            />
          ))}
        </div>
        <div style={addBlockStyle}>
          <InlineAddSearch
            label="+ Add brand"
            options={likeOptions}
            onPick={(brandId) => addBrand(brandId, 'like')}
            testId="settings-brands-like-add"
            emptyText="No more brands to add."
          />
        </div>
      </section>

      <section style={setgroupStyle} data-testid="settings-brands-avoid">
        <h4 style={h4Style}>Brands you avoid</h4>
        <p style={hintStyle}>
          Brands your household would rather skip — never hard-hidden, so you can
          always find them by search.
        </p>
        <div style={tagsRowStyle}>
          {avoided.length === 0 && (
            <span style={mutedStyle} data-testid="settings-brands-avoid-empty">
              No avoided brands yet.
            </span>
          )}
          {avoided.map((p) => (
            <RemovableChip
              key={p.brand_id}
              label={brandName(p.brand_id)}
              variant="avoid"
              onRemove={() =>
                void run(() => unlinkPref.mutateAsync(p.brand_id))
              }
              testId={`settings-brands-avoid-${p.brand_id}`}
            />
          ))}
        </div>
        <div style={addBlockStyle}>
          <InlineAddSearch
            label="+ Add brand"
            options={avoidOptions}
            onPick={(brandId) => addBrand(brandId, 'avoid')}
            testId="settings-brands-avoid-add"
            emptyText="No more brands to add."
          />
        </div>
      </section>

      <section style={setgroupStyle} data-testid="settings-products-like">
        <h4 style={h4Style}>Products you like</h4>
        <p style={hintStyle}>
          Specific product lines your household reaches for — search the catalog
          to add some.
        </p>
        <div style={tagsRowStyle}>
          {spLiked.length === 0 && (
            <span style={mutedStyle} data-testid="settings-products-like-empty">
              No liked products yet.
            </span>
          )}
          {spLiked.map((p) => (
            <RemovableChip
              key={p.systemproduct_id}
              label={p.name}
              variant="like"
              onRemove={() =>
                void run(() => unlinkSp.mutateAsync(p.systemproduct_id))
              }
              testId={`settings-products-like-${p.systemproduct_id}`}
            />
          ))}
        </div>
        <div style={addBlockStyle}>
          <SystemProductAddSearch
            label="+ Add product"
            linkedIds={spLinkedIds}
            onPick={(systemProductId) => addSystemProduct(systemProductId, 'like')}
            testId="settings-products-like-add"
          />
        </div>
      </section>

      <section style={setgroupStyle} data-testid="settings-products-avoid">
        <h4 style={h4Style}>Products you avoid</h4>
        <p style={hintStyle}>
          Specific product lines your household would rather skip — never
          hard-hidden, so you can always find them by search.
        </p>
        <div style={tagsRowStyle}>
          {spAvoided.length === 0 && (
            <span style={mutedStyle} data-testid="settings-products-avoid-empty">
              No avoided products yet.
            </span>
          )}
          {spAvoided.map((p) => (
            <RemovableChip
              key={p.systemproduct_id}
              label={p.name}
              variant="avoid"
              onRemove={() =>
                void run(() => unlinkSp.mutateAsync(p.systemproduct_id))
              }
              testId={`settings-products-avoid-${p.systemproduct_id}`}
            />
          ))}
        </div>
        <div style={addBlockStyle}>
          <SystemProductAddSearch
            label="+ Add product"
            linkedIds={spLinkedIds}
            onPick={(systemProductId) => addSystemProduct(systemProductId, 'avoid')}
            testId="settings-products-avoid-add"
          />
        </div>
      </section>

      <p style={{ ...mutedStyle, margin: '0 var(--space-1)' }}>
        Need to override one brand or product for a single product line? That
        lives on the product&apos;s own settings — these are the household-wide
        defaults.
      </p>
    </SettingsLayover>
  );
}
